import {
  createQuery,
  createSuspenseQuery
} from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

// Define the response schema based on the Swagger specification
const ClientSharedViewSchema = z.object({
  id: z.int(),
  category: z.string().min(1).nullish(),
  status: z.int().nullable(),
  tag: z.array(z.string()), // Assuming tag is an array of strings based on common usage
  name: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  website: z
    .string()
    // .regex(
    //   /^(http|https)?(:\/\/)?(www.)?[a-zA-Z0-9-_]+\.[a-zA-Z0-9-_]+(.[a-zA-Z]+)?/
    // )
    // .max(200)
    .nullish(),
  order: z.number().min(0).max(2147483647),
  team: z.string().min(1).nullish(),
  contact_state: z.string().min(1).nullish(),
  checklist_state: z.string().min(1).nullish(),
  aging: z.string().min(1).nullish(),
  reminder_state: z.string().min(1).nullish(),
  _group_value: z.string().nullish(),
  _group_count: z.int().nullish()
});

const ViewClientsResponseSchema = z.array(ClientSharedViewSchema);

// Define request schemas using discriminated union
const wfRequest = z.object({
  api: z.literal("wf"),
  id: z.string()
});
const publicRequest = z.object({
  api: z.literal("public"),
  slug: z.string(),
  id: z.string()
});
const ViewClientsRequestSchema = z.discriminatedUnion("api", [
  wfRequest,
  publicRequest
]);
const ViewClientsRequestVariables = z.union([
  wfRequest.omit({ api: true }),
  publicRequest.omit({ api: true })
]);

export type ViewClientsRequest = z.infer<typeof ViewClientsRequestSchema>;
export type ViewClientsResponse = z.infer<typeof ViewClientsResponseSchema>;

// URL mappings for wf and public APIs
const urls = {
  wf: "/views/:id/clients",
  public: "/public/:slug/boardviews/:id/clients"
};

export const useViewClients = createQuery<
  ViewClientsResponse,
  ViewClientsRequest,
  Error
>({
  queryKey: ["ViewClients"],
  staleTime: 1000 * 60, // 1 minute
  fetcher: async (variables) => {
    const { api: viewClientsAPI, ...rest } = variables;
    // The api helper likely substitutes :id and :slug based on the variables provided in `rest`
    return api<Omit<ViewClientsRequest, "api">, ViewClientsResponse>({
      method: "GET",
      url: urls[viewClientsAPI],
      requestSchema: ViewClientsRequestVariables,
      responseSchema: ViewClientsResponseSchema,
      body: rest // Assuming api helper handles GET params correctly
    });
  }
});

export const useViewClientsSuspense = createSuspenseQuery<
  ViewClientsResponse,
  ViewClientsRequest,
  Error
>({
  queryKey: ["ViewClientsSuspense"],
  staleTime: 1000 * 60, // 1 minute
  fetcher: async (variables) => {
    const { api: viewClientsAPI, ...rest } = variables;
    // The api helper likely substitutes :id and :slug based on the variables provided in `rest`
    return api<Omit<ViewClientsRequest, "api">, ViewClientsResponse>({
      method: "GET",
      url: urls[viewClientsAPI],
      requestSchema: ViewClientsRequestVariables,
      responseSchema: ViewClientsResponseSchema,
      body: rest // Assuming api helper handles GET params correctly
    });
  }
});
